import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { KeyRound, MailCheck, ShieldCheck } from "lucide-react";

/**
 * Customer / affiliate self-serve PIN reset dialog.
 * Step 1: press "Send OTP" — server mails a 6-digit code to the user's
 *         registered (verified) email. UI displays a masked preview.
 * Step 2: enter OTP + new 4-digit PIN + confirm → server verifies and saves.
 *
 * No admin approval anywhere in the loop.
 */
export default function PinResetSelfServeDialog({ open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState(1);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      // Reset on close to prevent stale state on next open.
      setStep(1); setCode(""); setPin(""); setConfirm(""); setMaskedEmail("");
    }
  }, [open]);

  const sendOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/pin-reset/send-otp", {});
      setMaskedEmail(data.masked_email || "");
      setStep(2);
      toast.success("Verification code sent to your email");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send code");
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!/^\d{6}$/.test(code)) return toast.error("Enter the 6-digit code from your email");
    if (!/^\d{4}$/.test(pin)) return toast.error("PIN must be exactly 4 digits");
    if (pin !== confirm) return toast.error("The two PINs don't match");
    setBusy(true);
    try {
      await api.post("/me/pin-reset/verify-and-set", { code, new_pin: pin, new_pin_confirm: confirm });
      toast.success("Redemption PIN updated");
      onSuccess?.();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reset PIN");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pin-reset-self-serve-dialog" className="bg-[#22103F] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-pink-400" strokeWidth={1.75} />
            {step === 1 ? "Reset your PIN" : "Enter code & new PIN"}
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-300">
              We'll email a 6-digit verification code to the address on your account. Once you enter it below, you can set a brand-new 4-digit PIN — no admin approval needed.
            </p>
            <div className="velo-surface p-3 rounded-lg flex items-start gap-2 text-xs text-slate-400">
              <MailCheck className="w-4 h-4 text-pink-400 mt-0.5 shrink-0" strokeWidth={2} />
              <span>Only the primary email on your Veloskin Agility Club profile will receive the code. Codes expire in 10 minutes.</span>
            </div>
            <DialogFooter>
              <Button
                data-testid="btn-send-pin-reset-otp"
                onClick={sendOtp}
                disabled={busy}
                className="w-full rounded-full bg-pink-500 hover:bg-pink-400 text-black h-11"
              >
                {busy ? "Sending…" : "Send verification code"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-2">
            {maskedEmail && (
              <div className="text-xs text-slate-400">Code sent to <span className="text-slate-200 font-mono">{maskedEmail}</span></div>
            )}
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-widest">6-digit code</Label>
              <Input
                data-testid="input-pin-reset-code"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="velo-input mt-2 h-12 text-center font-mono tracking-[0.4em] text-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-widest flex items-center gap-1.5"><KeyRound className="w-3 h-3" />New PIN</Label>
                <Input
                  data-testid="input-pin-reset-new"
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                  className="velo-input mt-2 h-12 text-center font-mono tracking-[0.5em] text-lg"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-widest">Confirm</Label>
                <Input
                  data-testid="input-pin-reset-confirm"
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))}
                  className="velo-input mt-2 h-12 text-center font-mono tracking-[0.5em] text-lg"
                />
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={sendOtp}
                disabled={busy}
                className="text-xs text-slate-400 hover:text-slate-200 underline underline-offset-2"
                data-testid="btn-pin-reset-resend"
              >
                Resend code
              </button>
              <Button
                data-testid="btn-pin-reset-submit"
                onClick={submit}
                disabled={busy}
                className="w-full sm:w-auto rounded-full bg-pink-500 hover:bg-pink-400 text-black h-11"
              >
                {busy ? "Saving…" : "Save new PIN"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
